/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Deva Sen. & Warren Dowey & Gerard Loughran
 */
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { OrganisationCognitoUser } from '@app/models/cognito-organisation-user.model';
import { environment } from '@env/environment';
import { SubSink } from 'subsink/dist/subsink';
import { ParameterStoreService } from '@app/services/aws-services/parameter-store.service';
import { AuthenticationService } from '@app/services/authentication/authentication.service';
import { OrganisationUsersModel } from '@app/models/organisation-users.model';
import { ApiService } from 'app/services/api-services/api.service';
import { SettingsService } from '@app/services/api-services/settings/settings.service';
import { ImageParserService } from '@app/services/shared-services/image-parser.service';
import { Router, ActivatedRoute, Event, NavigationEnd } from '@angular/router';
import { SettingsModel } from 'app/models/settings.model';
import { OrganisationUserService } from 'app/services/api-services/organisation-users/organisation-user.service';

export enum MenuViews {
  projects = 'projects',
  data = 'data',
  admin = 'admin',
}
@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() showSubmenuIcon: boolean = true;
  @Input() expandSubmenu: boolean = true;
  @Input() isMedicalDevice: boolean = false;
  @Output() onMenuToggle = new EventEmitter();

  public user: OrganisationCognitoUser;
  public viewProjectsPermission = this.auth.hasPermission(
    'ProjectsComponent',
    'view_projects_screen'
  );
  public viewAdminPermission = this.auth.hasPermission(
    'AdminDashboardComponent',
    'view_admin_screen'
  );
  public viewDatasetsPermission = this.auth.hasPermission(
    'DatasetsComponent',
    'view_datasets_screen'
  );
  public settings: SettingsModel;
  public imagingURL: string;
  public dashboardReady: boolean = false;

  // Needed for use accress multiple functions below
  public sonraiSuperUser: boolean;
  public appLogo: any = '../../../../assets/images/indra-icon.png';
  public hasLogo: boolean = false;
  public menuView: string;
  public menuViews = MenuViews;

  // Subscriptions to be unsubscribed to at the end for data leak purposes
  private subscriptionContainer = new SubSink();
  private superUserSubscription: Subscription;
  private settingSubscription: Subscription;
  private displaySettingSubscription: Subscription;

  constructor(
    public router: Router,
    public activatedRoute: ActivatedRoute,
    public auth: AuthenticationService,
    private settingsService: SettingsService,
    private organisationUserService: OrganisationUserService
  ) {
    // Subscribe to route change and check route to determine whether or not to display submenu
    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.checkRoutes();
      }
    });
  }

  ngOnInit() {
    // Get the user role permissions using the associated services
    this.user = this.auth.user;
    this.getDisplaySettings();
    this.getSuperUserPermission();
    this.checkRoutes();
    // getLogo is commented out for now until more db changes are made
    // this.getLogo();
  }

  getDisplaySettings() {
    // Ensure settings object is in the correct format
    this.settingsService.getSettings(environment.product_owner_uuid)
      .subscribe(settings => {
        this.settings = settings;
        this.imagingURL = 'https://' + this.settings.imagingUrl;
        this.dashboardReady = true;
      });
  }

  // Commented out for now as it is currently marked as needing more db changes
  // getLogo() {
  //   this.subscriptionContainer.sink = this.settingSubscription =
  //     this.settingsService.dataSubject.subscribe(
  //       (settings) => {
  //         let setting = settings[0];
  //         if (setting.app_logo != null && setting.app_logo !== '') {
  //           this.appLogo = this.imageParse.parse(setting.app_logo);
  //           this.hasLogo = true;
  //         }
  //         // Set default Indra logo if no appLogo exists in database
  //         else {
  //           this.appLogo = '../../../../assets/images/indra-icon.png';
  //           this.hasLogo = false;
  //         }
  //         if (this.settingSubscription) {
  //           this.settingSubscription.unsubscribe();
  //         }
  //       },
  //       // Set default Indra logo if there is an error with the request
  //       (error) => {
  //         this.appLogo = '../../../../assets/images/indra-icon.png';
  //         this.hasLogo = false;
  //         if (this.settingSubscription) {
  //           this.settingSubscription.unsubscribe();
  //         }
  //       }
  //     );
  //   this.settingsService.getSettingsByOrganisationId(environment.product_owner_uuid);
  // }

  getSuperUserPermission() {
    this.subscriptionContainer.sink = this.superUserSubscription = this.organisationUserService
      .getOrganisationUserByUserId(this.user.userId)
      .subscribe((data) => {
        this.sonraiSuperUser = data.sonraiSuperUser;
        if (this.superUserSubscription) {
          this.superUserSubscription.unsubscribe();
        }
      });
  }

  checkRoutes() {
    let menuView: string = '';
    // Check Project routes
    if (
      this.activatedRoute.snapshot['_urlSegment'].segments.length > 1 &&
      this.activatedRoute.snapshot['_urlSegment'].segments[0].path === 'project'
    ) {
      menuView = this.menuViews.projects;
    }
    // Check Data routes
    if (
      this.activatedRoute.snapshot['_routerState'].url === '/datasets' ||
      this.activatedRoute.snapshot['_routerState'].url === '/data-repo'
    ) {
      menuView = this.menuViews.data;
    }
    // Check Admin Routes
    if (
      this.activatedRoute.snapshot['_routerState'].url === '/admin-dashboard' ||
      (this.activatedRoute.snapshot['_urlSegment'].segments.length > 1 &&
        this.activatedRoute.snapshot['_urlSegment'].segments[0].path === 'admin')
    ) {
      menuView = this.menuViews.admin;
    }
    // Check if valid menuView is set then openSubmenu
    if (
      menuView === this.menuViews.projects ||
      menuView === this.menuViews.data ||
      menuView === this.menuViews.admin
    ) {
      // this.openSubmenu(menuView);
      this.openSubmenu();
    } else {
      this.showSubmenuIcon = false;
      this.expandSubmenu = false;
      this.onMenuToggle.emit(this.expandSubmenu);
    }
  }

  openSubmenu() {
    this.expandSubmenu = true;
    this.onMenuToggle.emit(this.expandSubmenu);
  }

  closeSubmenu() {
    this.expandSubmenu = false;
    this.onMenuToggle.emit(this.expandSubmenu);
  }

  ngOnDestroy() {
    this.subscriptionContainer.unsubscribe();
  }
}
